'use client';

import { useEffect, useRef, useState } from 'react';
import type { OttoChatMessage, OttoStep, OttoUiState } from '@/lib/otto';

// ── Otto — fenêtre de chat overlay (texte + vocal) ──────────────────────────
// Reste monté pour garder l'historique ; `open` contrôle l'affichage.
// Vocal : MediaRecorder → /api/otto/transcribe (faster-whisper hôte) → envoi direct.
// Quand l'agent renvoie un plan → onPlan(plan) : le parent ferme et rejoue.

import GooeyPopover from '@/components/GooeyPopover';
import { dicter } from '@/lib/dictee';
import { OndeDictee } from '@/components/OndeDictee';
import { MicButton } from '@/components/MicButton';
import { useLandscape } from '@/hooks/useLandscape';
import { NavPixels } from '@/components/NavPixels';

type Props = {
  open: boolean;
  onClose: () => void;
  getUiState: () => OttoUiState;
  onPlan: (plan: OttoStep[]) => void;
  // Message assistant local injecté à l'ouverture (ex : tooltip favoris → question + boutons).
  // Chaque NOUVEL objet seed est poussé une seule fois (comparaison par référence).
  seed?: { text: string; buttons: string[] } | null;
  // Texte utilisateur poussé par le parent et ENVOYÉ à l'ouverture (textarea
  // symptômes → conversation pré-remplie). Une fois par référence, comme seed.
  sendSeed?: { text: string } | null;
  // Texte pré-rempli dans le CHAMP (pas envoyé) : le récit dicté hors du chat
  // arrive déjà transcrit, relisible et corrigeable avant envoi. Une fois par référence.
  inputSeed?: { text: string } | null;
  // D'où Otto est sollicité (ex. 'test:demande') — envoyé au serveur : contexte
  // invisible pour l'utilisateur, stocké en session + injecté au prompt (§7.1bis).
  page?: string;
};

export default function OttoChat({ open, onClose, getUiState, onPlan, seed, sendSeed, inputSeed, page }: Props) {
  const [messages, setMessages] = useState<OttoChatMessage[]>([]);
  const [input, setInput] = useState('');
  // Session courante : id (reprise côté serveur) + titre LLM affiché dans l'en-tête
  const [convId, setConvId] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  // Vue « sessions précédentes » (flèche retour de l'en-tête)
  const [view, setView] = useState<'chat' | 'sessions'>('chat');
  const [sessions, setSessions] = useState<{ id: string; title: string | null; last_activity_at: string }[] | null>(null);
  const [status, setStatus] = useState<'idle' | 'recording' | 'transcribing' | 'thinking'>('idle');
  // Plan reçu mais volontairement retenu : « passe aux questions » n'a de sens
  // qu'une fois la liste des symptômes LUE. Il part à la fermeture (bouton ou croix).
  const [pendingPlan, setPendingPlan] = useState<OttoStep[] | null>(null);
  // Index du message dont la copie vient d'être confirmée (repasse à null seul).
  const [copied, setCopied] = useState<number | null>(null);
  const dicteeRef = useRef<ReturnType<typeof dicter> | null>(null);
  const startingRef = useRef(false); // double tap pendant l'autorisation micro
  const [level, setLevel] = useState(0); // amplitude de la voix (0 → 1), fait battre le bouton
  const scrollRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const busy = status === 'transcribing' || status === 'thinking';

  // La saisie grandit avec son contenu (max-h la plafonne) — une transcription
  // vocale entière reste visible au lieu de défiler dans une ligne unique.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [input]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, status, open]);

  // Chauffe le copilote dès l'ouverture : le 1er message tombe sur un pipe amorcé.
  // Même passe : signale au serveur d'où le chat est ouvert et recharge la
  // conversation chaude — continuité entre écrans et entre visites (§7.1bis).
  const warmed = useRef(false);
  useEffect(() => {
    if (!open || warmed.current) return;
    warmed.current = true;
    fetch('/api/otto/warmup', { method: 'POST' }).catch(() => {});
    fetch('/api/otto/history', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: page ?? 'inconnu' }),
    })
      .then(r => r.json())
      .then(d => {
        if (d?.id) setConvId(d.id);
        if (d?.title) setTitle(d.title);
        if (Array.isArray(d?.messages) && d.messages.length) {
          // L'historique passe devant ce qui est déjà local (seed éventuel)
          setMessages(m => [...d.messages, ...m]);
        }
      })
      .catch(() => {});
  }, [open, page]);

  // Seed local (question + boutons favoris) posé par le parent — pas d'appel API.
  // Pattern render-time (pas d'effect) : poussé une fois par référence de seed.
  const [lastSeed, setLastSeed] = useState<Props['seed']>(null);
  if (open && seed && seed !== lastSeed) {
    setLastSeed(seed);
    setMessages(m => [...m, { role: 'assistant', content: seed.text, buttons: seed.buttons }]);
  }

  // inputSeed : récit dicté hors du chat, déjà transcrit — il remplace le champ
  // (c'est un nouveau récit, pas un ajout à une saisie en cours).
  const lastInputSeed = useRef<Props['inputSeed']>(null);
  useEffect(() => {
    if (open && inputSeed && inputSeed !== lastInputSeed.current) {
      lastInputSeed.current = inputSeed;
      setInput(inputSeed.text);
    }
  }, [open, inputSeed]);

  // sendSeed : vrai message utilisateur envoyé à l'ouverture (fetch → effect, pas render-time).
  const lastSendSeed = useRef<Props['sendSeed']>(null);
  useEffect(() => {
    if (open && sendSeed && sendSeed !== lastSendSeed.current) {
      lastSendSeed.current = sendSeed;
      send(sendSeed.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- send est stable fonctionnellement, déclenché par référence de sendSeed
  }, [open, sendSeed]);

  // Whisper (faster-whisper hôte) → le champ de saisie, jamais l'envoi direct :
  // une transcription se relit avant de partir.
  async function finir(e: ReturnType<typeof dicter>) {
    setStatus('transcribing');
    try {
      const text = await e.arreter();
      if (text) setInput(prev => (prev ? `${prev} ${text}` : text));
    } catch { /* service tombé : le champ reste tel quel */ }
    setStatus('idle');
  }

  // Fermeture : un plan retenu part MAINTENANT — c'est le geste qui le déclenche.
  function closeChat() {
    const plan = pendingPlan;
    setPendingPlan(null);
    if (plan) onPlan(plan);
    onClose();
  }

  async function send(text: string) {
    const msg = text.trim();
    if (!msg || busy) return;
    setInput('');
    const history = messages.slice(-10);
    setMessages(m => [...m, { role: 'user', content: msg }]);
    await ask(msg, history);
  }

  /** L'aller-retour avec Otto, séparé de l'affichage de la bulle utilisateur : rejouer
   *  une demande ne doit pas la réafficher une deuxième fois. */
  async function ask(msg: string, history: OttoChatMessage[]) {
    setStatus('thinking');
    try {
      const res = await fetch('/api/otto/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history, uiState: getUiState(), page, conversationId: convId ?? undefined }),
      });
      const data = await res.json();
      const reply: string = data?.reply ?? 'Réponse illisible, réessaie.';
      if (data?.conversationId) setConvId(data.conversationId);
      if (data?.title) setTitle(data.title);
      setMessages(m => [...m, { role: 'assistant', content: reply }]);
      setStatus('idle');
      if (Array.isArray(data?.plan) && data.plan.length) {
        // « Passe aux questions » se lit d'abord (la liste des symptômes est la
        // réponse elle-même) : ce plan attend la fermeture. Les autres se rejouent
        // après un battement, le temps de lire « Commande reçue ».
        if (data.plan.some((s: OttoStep) => s.action === 'startDiagQuestions')) setPendingPlan(data.plan);
        else setTimeout(() => onPlan(data.plan), 900);
      }
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Connexion à Otto impossible. Réessaie.' }]);
      setStatus('idle');
    }
  }

  /** Copie le message entier. `clipboard` n'existe pas hors contexte sécurisé
   *  (dev en http://) : repli sur une zone de texte jetable, sinon le bouton ne
   *  ferait rien sur la moitié des postes. */
  async function copyMessage(i: number, text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.className = 'fixed opacity-0 pointer-events-none';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* rien de plus à tenter */ }
      ta.remove();
    }
    setCopied(i);
    setTimeout(() => setCopied(c => (c === i ? null : c)), 1400);
  }

  /** Reprend un message : il repart dans la zone de saisie et TOUT ce qui suivait
   *  disparaît. Otto doit répondre à la question corrigée, pas à la conversation
   *  qu'avait provoquée l'ancienne — c'est le sens de « modifier et rejouer ». */
  function editMessage(i: number) {
    if (busy) return;
    setInput(messages[i].content);
    setMessages(m => m.slice(0, i));
    setPendingPlan(null);
    requestAnimationFrame(() => taRef.current?.focus());
  }

  /** Renvoie le message `i` tel quel : tout ce qui suivait est jeté et Otto rejoue
   *  à partir de là. Le modèle n'étant pas déterministe, redemander la même chose
   *  suffit souvent quand la réponse est passée à côté. */
  async function replayFrom(i: number) {
    if (busy || messages[i]?.role !== 'user') return;
    const kept = messages.slice(0, i + 1);
    setMessages(kept);
    setPendingPlan(null);
    await ask(messages[i].content, kept.slice(0, -1).slice(-10));
  }

  /** Bouton de la dernière réponse d'Otto : rejoue la demande qui l'a produite. */
  const regenerate = () => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'user') return replayFrom(i);
  };

  async function openSessions() {
    setView('sessions');
    setSessions(null);
    try {
      const r = await fetch('/api/otto/sessions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await r.json();
      setSessions(Array.isArray(d?.sessions) ? d.sessions : []);
    } catch {
      setSessions([]);
    }
  }

  async function resumeSession(id: string) {
    try {
      const r = await fetch('/api/otto/sessions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }),
      });
      const d = await r.json();
      if (Array.isArray(d?.messages)) {
        setMessages(d.messages);
        setConvId(d.id);
        setTitle(d.title ?? null);
        setView('chat');
      }
    } catch { /* on reste sur la liste */ }
  }

  // Arrêt décidé sur l'enregistrement RÉEL (la ref), pas sur un state React qui,
  // désynchronisé, rendrait l'enregistrement impossible à arrêter.
  async function toggleRecording() {
    const e = dicteeRef.current;
    if (e) {
      dicteeRef.current = null;
      // Remplit le champ (corrigeable) — le même bouton devient l'avion d'envoi
      await finir(e);
      return;
    }
    if (busy || startingRef.current) return;
    startingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Découpée aux silences : chaque tranche part pendant qu'on parle encore.
      dicteeRef.current = dicter(stream, setLevel);
      setStatus('recording');
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Micro inaccessible. Vérifie les permissions du navigateur.' }]);
    } finally {
      startingRef.current = false;
    }
  }

  // Popover gooey ancré au bouton robot (#otto-fab) — sort du bouton, backdrop blur.
  // Toujours monté : GooeyPopover joue la résorption quand `open` retombe.
  const anchorEl = typeof document !== 'undefined' ? document.getElementById('otto-fab') : null;

  /**
   * Couché, le chat ne s'ouvre plus AU-DESSUS du bouton mais À CÔTÉ : la hauteur
   * est justement ce qui manque en paysage, en prendre au-dessus du bouton ne
   * laisserait qu'une bande. `side` donne au panneau toute la hauteur de l'écran
   * et le pose du côté où il reste de la place.
   *
   * Les dimensions viennent de `useLandscape` et non de `window` : lu au rendu,
   * `window.innerWidth` n'était jamais relu à la rotation (rien ne re-rendait ce
   * composant), et le panneau gardait la largeur de l'orientation précédente.
   * Le hook, lui, republie ses valeurs — et porte les rattrapages qu'il faut
   * pour l'annonce tardive d'iOS.
   */
  const { landscape, w: vw } = useLandscape();
  const panelW = Math.min(Math.round((vw || 360) * (landscape ? 0.52 : 0.95)), 640);

  return (
    <GooeyPopover open={open} onClose={closeChat} anchorEl={anchorEl} width={panelW}
      placement={landscape ? 'side' : 'above'}
      /* Le chat monte aussi haut que la place le permet, et c'est la marge
         haute — et non un plafond en pourcentage — qui l'arrête : il se range
         juste sous la bascule jour/nuit, quel que soit l'écran. */
      maxHeight={1200} vhCap={0.95} topInset={64} color="var(--goo-surface)"
      cloneIcon={<>
        <span aria-hidden className="absolute inset-0 opacity-60 pointer-events-none"><NavPixels color="var(--otto-pixel)" overlay={false} radius={24} /></span>
        <span className="relative"><OttoIcon /></span>
      </>}>
      {/* Fond : EXACTEMENT la matière du gooey (--goo-surface, opaque). Un fond
          translucide s'éclaircissait de ce qu'il y avait dessous, et la goutte
          paraissait alors plus noire que le panneau. Neutre depuis le 21/10
          (Zidan) : la trame de pixels reste sur le bouton, pas derrière les
          conversations. */}
      <div aria-hidden className="absolute inset-0 pointer-events-none rounded-[var(--goo-r)] bg-[var(--goo-surface)]" />
      {/* En-tête — overlay translucide : les messages défilent DESSOUS, floutés par le blur */}
      {/* `px-[22px] pt-[22px]` et pas `p-3` : les lueurs des bosses portent à
          21 px vers le HAUT-GAUCHE, leur ombre sombre à ~18 px vers le bas-droite.
          Avec 12-14 px de marge elles sortaient de la matière et s'arrêtaient net
          sur la courbe de l'angle — les « coupures » du haut et des côtés
          signalées le 10/08. 22 px de chaque côté, et plus rien ne dépasse :
          c'est la matière qui manquait sous la lueur, pas un rognage qui
          manquait. Même logique que le `p-[22px]` du choix de la bourse. */}
      {/* L'ombre du bandeau n'est PAS une box-shadow : c'est le `after` en fondu
          du feed (`--header-fade`), posé sous la barre. Une box-shadow diffuse
          dans les quatre directions — elle sortait de la matière en haut et sur
          les côtés et se lisait comme un calque rapporté ; le fondu, lui,
          s'éteint tout seul exactement au pourtour, sans jamais toucher un bord. */}
      {/* L'ombre du bandeau est ADOUCICIE ici (`after:opacity-55`) : à pleine
          intensité le fondu se lisait comme une box-shadow trop marquée sous
          la barre — signalé le 16/08. Même retrait sur la barre du bas, et
          identique sur les trois interfaces : c'est CE composant, unique, qui
          les sert toutes. */}
      <div className="absolute -top-px inset-x-0 z-10 flex items-center justify-between px-[22px] pt-[22px] pb-2.5 border-b border-[var(--b-1)] backdrop-blur-lg rounded-t-[var(--goo-r)] after:content-[''] after:absolute after:inset-x-0 after:top-[calc(100%+1px)] after:h-6 after:pointer-events-none after:bg-[image:var(--header-fade)] after:opacity-55">
        <span className="flex items-center gap-2 min-w-0">
          {view === 'chat' ? (
            <button type="button" onClick={openSessions} aria-label="Sessions précédentes"
              className="relative w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-[var(--ink-2)] active:scale-95 transition"><span aria-hidden style={{ filter: 'blur(2.5px)' }} className="absolute inset-0 rounded-full shadow-[var(--sh-emboss)]" />
              <svg className="relative" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          ) : (
            <button type="button" onClick={() => setView('chat')} aria-label="Revenir au chat"
              className="relative w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-[var(--accent-ink)] active:scale-95 transition"><span aria-hidden style={{ filter: 'blur(2.5px)' }} className="absolute inset-0 rounded-full shadow-[var(--sh-emboss)]" />
              <svg className="relative" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          )}
          <span className="relative w-9 h-9 shrink-0 rounded-full text-[var(--accent-ink)] flex items-center justify-center"><span aria-hidden style={{ filter: 'blur(2.5px)' }} className="absolute inset-0 rounded-full shadow-[var(--sh-emboss)]" /><span className="relative"><OttoIcon /></span></span>
          <span className="min-w-0">
            <span className="block text-sm font-black leading-tight">Otto</span>
            <span className="block text-xs text-[var(--ink-3)] truncate">
              {view === 'sessions' ? 'Sessions précédentes' : (title ?? 'Assistant OGRYN')}
            </span>
          </span>
        </span>
        {/* Croix en rouge, comme dans la fenêtre véhicule : c'est le seul geste de
            cette barre qui referme tout. */}
        <button type="button" onClick={closeChat} aria-label="Fermer"
          className="emboss relative w-9 h-9 shrink-0 rounded-full flex items-center justify-center active:scale-95 transition">
          <span className="relative">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.8" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
          </span>
        </button>
      </div>

      {/* Liste des sessions (flèche retour) — même cadre que les messages */}
      {view === 'sessions' && (
        <div className="absolute inset-0 overflow-y-auto px-4 pt-[80px] pb-[96px] space-y-2 no-scrollbar">
          {sessions === null && <div className="text-center text-sm text-[var(--ink-3)] pt-10">Chargement…</div>}
          {sessions?.length === 0 && <div className="text-center text-sm text-[var(--ink-3)] pt-10">Aucune session pour l'instant.</div>}
          {sessions?.map(sess => (
            <button key={sess.id} type="button" onClick={() => resumeSession(sess.id)}
              className={`w-full text-left rounded-2xl px-4 py-3 border transition active:scale-[0.98] ${
                sess.id === convId ? 'bg-emerald-500/12 border-emerald-500/45' : 'bg-[var(--s-1)] border-[var(--b-1)]'
              }`}>
              <span className="block text-sm font-bold truncate">{sess.title ?? 'Conversation'}</span>
              <span className="block text-xs text-[var(--ink-3)] mt-0.5">
                {new Date(sess.last_activity_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                {sess.id === convId ? ' · en cours' : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className={`absolute inset-0 overflow-y-auto px-4 pt-[80px] pb-[96px] space-y-3 no-scrollbar ${view === 'sessions' ? 'invisible' : ''}`}>
        {messages.length === 0 && (
          <div className="text-center text-sm text-[var(--ink-3)] pt-10 px-6 leading-relaxed">
            Dis-moi ce qu'il faut réparer, où tu es et tes exigences — je m'occupe de toute la demande.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
            {/* Bulles opaques : jaune itinéraires = utilisateur,
                vert charte = Otto. whitespace-pre-line : les retours à la ligne du LLM s'affichent. */}
            <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line shadow-[var(--sh-1)] ${
              m.role === 'user' ? 'bg-amber-400 text-stone-900 rounded-br-md' : 'bg-[var(--brand)] text-white rounded-bl-md'
            }`}>
              {m.content}
            </div>
            {/* Actions du message. Toujours visibles : sur mobile il n'y a pas de survol
                pour les faire apparaître, et un bouton qu'on doit deviner n'existe pas. */}
            <div className={`flex items-center gap-0.5 mt-0.5 ${m.role === 'user' ? 'flex-row-reverse pr-1' : 'pl-1'}`}>
              <button type="button" onClick={() => copyMessage(i, m.content)}
                aria-label={copied === i ? 'Message copié' : 'Copier le message'}
                className="p-1.5 rounded-lg text-[var(--ink-3)] hover:text-[var(--ink-1)] active:scale-90 transition">
                {copied === i ? <CheckIcon /> : <CopyIcon />}
              </button>
              {m.role === 'user' && !busy && (
                <>
                  <button type="button" onClick={() => editMessage(i)} aria-label="Modifier ce message et rejouer la suite"
                    className="p-1.5 rounded-lg text-[var(--ink-3)] hover:text-[var(--ink-1)] active:scale-90 transition">
                    <PencilIcon />
                  </button>
                  <button type="button" onClick={() => replayFrom(i)} aria-label="Renvoyer ce message tel quel"
                    className="p-1.5 rounded-lg text-[var(--ink-3)] hover:text-[var(--ink-1)] active:scale-90 transition">
                    <RetryIcon />
                  </button>
                </>
              )}
              {m.role === 'assistant' && i === messages.length - 1 && !busy && (
                <button type="button" onClick={regenerate} aria-label="Redemander une réponse"
                  className="p-1.5 rounded-lg text-[var(--ink-3)] hover:text-[var(--ink-1)] active:scale-90 transition">
                  <RetryIcon />
                </button>
              )}
            </div>
            {/* Choix rapides (favoris…) — actifs uniquement sur le dernier message */}
            {m.role === 'assistant' && !!m.buttons?.length && i === messages.length - 1 && !busy && (
              <div className="flex flex-wrap gap-1.5 mt-2 max-w-[86%]">
                {m.buttons.map(b => (
                  <button key={b} type="button" onClick={() => send(b)}
                    className="px-3 py-2 rounded-full text-sm font-bold bg-emerald-500/12 border border-emerald-500/45 text-[var(--accent-ink)] active:scale-95 transition">
                    {b}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {/* Relance du plan retenu : c'est le geste « je ferme » rendu cliquable —
            fermer par la croix fait exactement la même chose. */}
        {pendingPlan && !busy && (
          <div className="flex justify-start">
            <button type="button" onClick={closeChat}
              className="max-w-[86%] flex items-center gap-2 rounded-2xl rounded-bl-md px-3.5 py-3 text-sm font-bold bg-emerald-500 text-white shadow-[var(--sh-1)] active:scale-[0.98] transition">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="relative shrink-0">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Fermer et passer aux questions
            </button>
          </div>
        )}
        {/* La transcription n'est PAS un message : c'est le champ de saisie qui se
            remplit. Elle vivait ici comme une bulle envoyée, alors que rien n'a
            encore été envoyé — son attente se joue dans le champ (voir plus bas). */}
        {status === 'thinking' && (
          <div className="flex justify-start">
            <div className="bg-[var(--brand)]/85 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-[var(--ink-1)] flex items-center gap-2 shadow-[var(--sh-1)]">
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
              Otto réfléchit…
            </div>
          </div>
        )}
      </div>

      {/* Saisie — UN SEUL bouton : micro quand le champ est vide, avion (envoi) sinon.
          Le vocal remplit le champ (corrigeable) puis s'envoie avec l'avion. */}
      {/* Ombre du bandeau haut, retournée : les messages passent DESSOUS les deux
          bandeaux, il faut que ça se voie des deux côtés. Même technique que
          l'en-tête — un `after` en fondu (`--footer-fade`, miroir de
          `--header-fade`) posé AU-DESSUS de la barre, jamais une box-shadow :
          elle se projetait hors de la matière en bas et sur les côtés.
          `calc(100%+1px)` et pas `bottom-full` : celui-ci se mesure SANS la
          bordure, l'ombre recouvrait le séparateur (21/10) ; le trait passe devant.
          `px-[22px] pb-[22px]` : le relief du micro et de l'envoi projette sa
          lueur vers le haut-gauche et son ombre vers le bas — avec 12 px de
          marge, il sortait de la matière (le « halo » du 10/08). 22 px, comme
          l'en-tête. */}
      <div className="absolute -bottom-px inset-x-0 z-10 px-[22px] pb-[22px] pt-2.5 border-t border-[var(--b-1)] flex items-end gap-2 backdrop-blur-lg rounded-b-[var(--goo-r)] after:content-[''] after:absolute after:inset-x-0 after:bottom-[calc(100%+1px)] after:h-6 after:pointer-events-none after:bg-[image:var(--footer-fade)] after:opacity-55">
        {/* À plat sur le voile, angles vifs : le champ du wizard véhicule (21/10). */}
        <div className="field-neumo relative flex-1 min-w-0 rounded-sm">
          {/* Le loader se pose À LA PLACE du placeholder, sur la première ligne et
              rien de plus. La version précédente couvrait tout le champ d'un fond
              `--s-2` : ça se lisait comme un bouton apparu dans la zone de texte.
              Ni fond, ni cadre — juste ce que le placeholder aurait écrit. */}
          {status === 'transcribing' && (
            <span className="absolute left-4 top-3.5 z-[2] flex items-center gap-2 text-sm text-[var(--ink-4)] pointer-events-none">
              <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
              Transcription…
            </span>
          )}
          {/* Pas de placeholder pendant l'enregistrement : le micro qui bat DIT
              déjà qu'on écoute, et le champ est désactivé — une phrase de plus
              n'ajoute qu'un texte à lire pour rien. */}
          <OndeDictee actif={status === 'recording'} level={level}
            className="absolute inset-x-4 top-3 h-6 z-[2] text-[var(--ink-2)]" />
          <textarea
            ref={taRef}
            value={input}
            rows={1}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder={status === 'transcribing' || status === 'recording' ? ''
              : 'Écris ou dicte ta demande…'}
            disabled={status === 'recording'}
            className={`w-full block min-h-12 max-h-[132px] bg-transparent rounded-sm border-0 px-4 py-3.5 text-sm leading-snug outline-none placeholder:text-[var(--ink-4)] resize-none${status === 'recording' ? ' text-transparent' : ''}`}
          />
        </div>
        {status === 'recording' ? (
          <MicButton recording level={level} onToggle={toggleRecording} />
        ) : input.trim() ? (
          /* Même bosse que le micro et la croix : trois boutons de la même barre
             ne peuvent pas être de trois matières différentes. C'est l'icône qui
             porte le vert, pas une pastille pleine. */
          <button type="button" onClick={() => send(input)} disabled={busy} aria-label="Envoyer"
            className="relative w-12 h-12 rounded-full text-[var(--otto-ink)] flex items-center justify-center shrink-0 active:scale-95 transition disabled:opacity-40">
            <span aria-hidden style={{ filter: 'blur(2.5px)' }} className="absolute inset-0 rounded-full shadow-[var(--sh-emboss)]" />
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="relative -translate-y-[1px]"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        ) : (
          /* Le vert du micro au repos est celui de l'ENVOI (`--otto-ink`), pas
             `--accent-ink` : deux boutons côte à côte dans la même barre, dont
             l'un remplace l'autre selon qu'on écrit ou qu'on dicte, ne peuvent
             pas être de deux verts différents. */
          <MicButton recording={false} level={0} onToggle={toggleRecording} disabled={busy} />
        )}
      </div>
    </GooeyPopover>
  );
}

export function OttoIcon({ size = 20 }: { size?: number }) {
  // Tête de robot Otto (remplace l'ancien pictogramme « soleil »)
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="13" rx="2"/>
      <path d="M12 8V5"/><circle cx="12" cy="4" r="1.5" fill="currentColor"/>
      <circle cx="8.5" cy="14" r="1.5" fill="currentColor" stroke="none"/>
      <circle cx="15.5" cy="14" r="1.5" fill="currentColor" stroke="none"/>
      <path d="M9 18h6"/>
    </svg>
  );
}

// Actions de message : copier, confirmation de copie, reprendre, redemander.
function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z"/>
    </svg>
  );
}

function RetryIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
    </svg>
  );
}

